import { prettyFloat } from 'webui/utils/number';
import { arrayGetSet, createAccessors } from 'z3k-parser/lib/accessories';
import { isFlagSet, setFlag } from 'z3k-parser/lib/flags';
import { convertThreshold } from 'z3k-parser/model/z3k-config/sensors/utils';
export var AnalogInput;
(function (AnalogInput) {
    let Type;
    (function (Type) {
        Type[Type["ANALOG_INPUT"] = 0] = "ANALOG_INPUT";
        Type[Type["PRESSURE_SENSOR_5"] = 1] = "PRESSURE_SENSOR_5";
        Type[Type["PRESSURE_SENSOR_12"] = 2] = "PRESSURE_SENSOR_12";
        Type[Type["MAGNET"] = 3] = "MAGNET";
        Type[Type["MOTION_CONTROLLED"] = 4] = "MOTION_CONTROLLED";
        Type[Type["SMOKE"] = 5] = "SMOKE";
        Type[Type["LEAK"] = 6] = "LEAK";
        Type[Type["MOTION_UNCONTROLLED"] = 7] = "MOTION_UNCONTROLLED";
        Type[Type["THERMOSTAT"] = 8] = "THERMOSTAT";
        Type[Type["BOILER_FAILURE_PLUS"] = 9] = "BOILER_FAILURE_PLUS";
        Type[Type["BOILER_FAILURE_MINUS"] = 10] = "BOILER_FAILURE_MINUS";
        Type[Type["IGNITION"] = 11] = "IGNITION";
        Type[Type["SPEED_SENSOR"] = 12] = "SPEED_SENSOR";
        Type[Type["RPM_SENSOR"] = 13] = "RPM_SENSOR";
        Type[Type["DISCRETE"] = 14] = "DISCRETE";
        Type[Type["ALARM_BUTTON"] = 15] = "ALARM_BUTTON";
        Type[Type["FUEL_STAT_SENSOR"] = 16] = "FUEL_STAT_SENSOR";
        Type[Type["HUMIDITY_SENSOR"] = 17] = "HUMIDITY_SENSOR";
        Type[Type["PRESSURE_SENSOR_MLD_6"] = 18] = "PRESSURE_SENSOR_MLD_6";
        Type[Type["ANALOG_DISCRETE_SENSOR_NO"] = 19] = "ANALOG_DISCRETE_SENSOR_NO";
        Type[Type["ANALOG_DISCRETE_SENSOR_NC"] = 20] = "ANALOG_DISCRETE_SENSOR_NC";
        Type[Type["PRESSURE_SENSOR_MLD_10"] = 21] = "PRESSURE_SENSOR_MLD_10";
    })(Type = AnalogInput.Type || (AnalogInput.Type = {}));
    let Flags;
    (function (Flags) {
        Flags[Flags["NO_GUARD_CTRL_ENABLE"] = 1] = "NO_GUARD_CTRL_ENABLE";
        Flags[Flags["NO_MAIN_SUPPLY_CTRL_ENABLE"] = 2] = "NO_MAIN_SUPPLY_CTRL_ENABLE";
        Flags[Flags["RESISTANCE_CTRL"] = 4] = "RESISTANCE_CTRL";
        Flags[Flags["EVENT_TO_SERVER"] = 8] = "EVENT_TO_SERVER";
        Flags[Flags["USE_CALIBRATION_TABLE"] = 16] = "USE_CALIBRATION_TABLE";
        Flags[Flags["USE_DATA_FROM_DIAGNOSTICS"] = 32] = "USE_DATA_FROM_DIAGNOSTICS";
        Flags[Flags["IS_WORKER_TRIGGERED_FLAG"] = 64] = "IS_WORKER_TRIGGERED_FLAG";
        Flags[Flags["IS_AMPER_INPUT"] = 128] = "IS_AMPER_INPUT";
    })(Flags = AnalogInput.Flags || (AnalogInput.Flags = {}));
    let StatusFlags;
    (function (StatusFlags) {
        StatusFlags[StatusFlags["GUARD_FLAG"] = 1] = "GUARD_FLAG";
        StatusFlags[StatusFlags["IS_TRIGGERED_FLAG"] = 2] = "IS_TRIGGERED_FLAG";
        StatusFlags[StatusFlags["IS_RESET_FLAG"] = 4] = "IS_RESET_FLAG";
        StatusFlags[StatusFlags["TRIGGERED_EVENT_FLAG"] = 8] = "TRIGGERED_EVENT_FLAG";
        StatusFlags[StatusFlags["MAIN_VOLTAGE_LOW_FLAG"] = 16] = "MAIN_VOLTAGE_LOW_FLAG";
        StatusFlags[StatusFlags["RESISTANCE_CTRL_FLAG"] = 32] = "RESISTANCE_CTRL_FLAG";
        StatusFlags[StatusFlags["PRESSURE_CTRL_FLAG"] = 64] = "PRESSURE_CTRL_FLAG";
        StatusFlags[StatusFlags["DAMAGED_WIRE_CTRL_FLAG"] = 128] = "DAMAGED_WIRE_CTRL_FLAG";
        StatusFlags[StatusFlags["RECOVERED_EVENT_FLAG"] = 256] = "RECOVERED_EVENT_FLAG";
        StatusFlags[StatusFlags["IS_UPPER_THREES_FLAG"] = 512] = "IS_UPPER_THREES_FLAG";
        StatusFlags[StatusFlags["EXT_BOARD_CONNECTION_LOST"] = 1024] = "EXT_BOARD_CONNECTION_LOST";
        StatusFlags[StatusFlags["LITERS_OVER_ONE_HUNDRED_KM"] = 2048] = "LITERS_OVER_ONE_HUNDRED_KM";
        StatusFlags[StatusFlags["TRIGGERED_INDICATE_FLAG"] = 4096] = "TRIGGERED_INDICATE_FLAG";
    })(StatusFlags = AnalogInput.StatusFlags || (AnalogInput.StatusFlags = {}));
    const FixedVoltageSensors = [
        Type.PRESSURE_SENSOR_5,
        Type.PRESSURE_SENSOR_12,
        Type.PRESSURE_SENSOR_MLD_6,
        Type.MAGNET,
        Type.MOTION_CONTROLLED,
        Type.SMOKE,
        Type.LEAK,
        Type.MOTION_UNCONTROLLED,
        Type.THERMOSTAT,
        Type.BOILER_FAILURE_PLUS,
        Type.BOILER_FAILURE_MINUS,
        Type.DISCRETE,
        Type.ALARM_BUTTON,
        Type.ANALOG_DISCRETE_SENSOR_NO,
        Type.ANALOG_DISCRETE_SENSOR_NC,
        Type.PRESSURE_SENSOR_MLD_10,
    ];
    function voltFromSettingRegister(input) {
        let komVoltMul = 1;
        if (input.sensor_type === Type.ANALOG_INPUT)
            (input.setting_register & 0x4) > 0
                ? (komVoltMul = 1000)
                : (komVoltMul = 10);
        if (FixedVoltageSensors.includes(input.sensor_type))
            komVoltMul = 10;
        return komVoltMul;
    }
    AnalogInput.voltFromSettingRegister = voltFromSettingRegister;
    let Units;
    (function (Units) {
        Units[Units["VOLTAGE"] = 0] = "VOLTAGE";
        Units[Units["RESISTANCE"] = 1] = "RESISTANCE";
        Units[Units["PRESSURE"] = 2] = "PRESSURE";
        Units[Units["SPEED"] = 3] = "SPEED";
        Units[Units["RPM"] = 4] = "RPM";
        Units[Units["VOLUME"] = 5] = "VOLUME";
        Units[Units["FUEL_RATE"] = 6] = "FUEL_RATE";
        Units[Units["PERCENT"] = 7] = "PERCENT";
        Units[Units["UNITLESS"] = 8] = "UNITLESS";
        Units[Units["AMPER"] = 9] = "AMPER";
        Units[Units["FREQUENCY"] = 10] = "FREQUENCY";
        Units[Units["TEMPERATURE"] = 11] = "TEMPERATURE";
        Units[Units["STREAM"] = 12] = "STREAM";
        Units[Units["PPM"] = 13] = "PPM";
        Units[Units["REACTIVE_POWER"] = 14] = "REACTIVE_POWER";
        Units[Units["ACTIVE_POWER"] = 15] = "ACTIVE_POWER";
        Units[Units["FULL_POWER"] = 16] = "FULL_POWER";
        Units[Units["ENERGY"] = 17] = "ENERGY";
        Units[Units["PHASE_SHIFT"] = 18] = "PHASE_SHIFT";
        Units[Units["MILLIAMPER"] = 19] = "MILLIAMPER";
    })(Units = AnalogInput.Units || (AnalogInput.Units = {}));
    AnalogInput.a300FrequencySensorTypes = [
        Type.SPEED_SENSOR,
        Type.RPM_SENSOR,
        Type.FUEL_STAT_SENSOR,
    ];
    AnalogInput.a300SensorTypes = [
        ...AnalogInput.a300FrequencySensorTypes,
        Type.IGNITION,
        Type.DISCRETE,
        Type.ALARM_BUTTON,
    ];
    AnalogInput.booleanSensorsTypes = [
        Type.MAGNET,
        Type.MOTION_CONTROLLED,
        Type.SMOKE,
        Type.LEAK,
        Type.MOTION_UNCONTROLLED,
        Type.THERMOSTAT,
        Type.BOILER_FAILURE_PLUS,
        Type.BOILER_FAILURE_MINUS,
        Type.IGNITION,
        Type.DISCRETE,
        Type.ALARM_BUTTON,
        Type.ANALOG_DISCRETE_SENSOR_NO,
        Type.ANALOG_DISCRETE_SENSOR_NC,
    ];
    AnalogInput.typesWithThresholds = [
        Type.ANALOG_INPUT,
        Type.PRESSURE_SENSOR_5,
        Type.PRESSURE_SENSOR_12,
        Type.PRESSURE_SENSOR_MLD_6,
        Type.DISCRETE,
        Type.HUMIDITY_SENSOR,
        Type.PRESSURE_SENSOR_MLD_10,
    ];
    AnalogInput.typesWithThresholdLimits = [
        Type.ANALOG_INPUT,
        Type.PRESSURE_SENSOR_5,
        Type.PRESSURE_SENSOR_12,
        Type.PRESSURE_SENSOR_MLD_6,
        Type.HUMIDITY_SENSOR,
        Type.PRESSURE_SENSOR_MLD_10,
    ];
    AnalogInput.typesWithActivityTimeouts = [
        Type.ANALOG_INPUT,
        Type.HUMIDITY_SENSOR,
        Type.PRESSURE_SENSOR_12,
        Type.PRESSURE_SENSOR_MLD_6,
        Type.PRESSURE_SENSOR_5,
        Type.PRESSURE_SENSOR_MLD_10,
    ];
    const pressureTypes = [
        Type.PRESSURE_SENSOR_5,
        Type.PRESSURE_SENSOR_12,
        Type.PRESSURE_SENSOR_MLD_6,
        Type.PRESSURE_SENSOR_MLD_10,
    ];
    function statusFlagSet(state, flag) {
        return (state.flag & flag) !== 0;
    }
    AnalogInput.statusFlagSet = statusFlagSet;
    function isWorkingSensor(sensor) {
        return isFlagSet(sensor, Flags.IS_WORKER_TRIGGERED_FLAG);
    }
    AnalogInput.isWorkingSensor = isWorkingSensor;
    function setWorkingSensor(sensor, checked) {
        return setFlag(sensor, Flags.IS_WORKER_TRIGGERED_FLAG, checked);
    }
    AnalogInput.setWorkingSensor = setWorkingSensor;
    function getValue(ai, state) {
        const { value = 0 } = state;
        const type = getType(ai);
        if (isPressureInput(ai)) {
            return value * 0.1;
        }
        if (type === Type.HUMIDITY_SENSOR) {
            return value;
        }
        if (type === Type.ANALOG_INPUT) {
            if (isUseResistance(ai)) {
                return value * 0.001;
            }
        }
        if (AnalogInput.a300FrequencySensorTypes.includes(type)) {
            return [Type.RPM_SENSOR, Type.FUEL_STAT_SENSOR].includes(type)
                ? (value * 0.1) / ai.impulse_frequency
                : value / ai.impulse_frequency;
        }
        return value / getMultiplier(ai);
    }
    AnalogInput.getValue = getValue;
    function getValueHuman(ai, state) {
        return prettyFloat(getValue(ai, state), 1);
    }
    AnalogInput.getValueHuman = getValueHuman;
    function setNewType(analog_input, type) {
        if (type === AnalogInput.Type.THERMOSTAT) {
            analog_input = setFlag(analog_input, Flags.NO_GUARD_CTRL_ENABLE, true);
        }
        analog_input = setFlag(analog_input, Flags.RESISTANCE_CTRL, false);
        return setUnit({ ...analog_input, sensor_type: type });
    }
    AnalogInput.setNewType = setNewType;
    function isTriggered(state) {
        return statusFlagSet(state, StatusFlags.IS_TRIGGERED_FLAG);
    }
    AnalogInput.isTriggered = isTriggered;
    function isWorked(state) {
        return statusFlagSet(state, StatusFlags.TRIGGERED_INDICATE_FLAG);
    }
    AnalogInput.isWorked = isWorked;
    function isUseCalibrationTable(analogInput) {
        return isFlagSet(analogInput, Flags.USE_CALIBRATION_TABLE);
    }
    AnalogInput.isUseCalibrationTable = isUseCalibrationTable;
    function isUseResistance(analog_input) {
        return isFlagSet(analog_input, Flags.RESISTANCE_CTRL);
    }
    AnalogInput.isUseResistance = isUseResistance;
    function setUseResistance(ai, use) {
        return setFlag(ai, Flags.RESISTANCE_CTRL, use);
    }
    AnalogInput.setUseResistance = setUseResistance;
    function isDamageWire(state) {
        return statusFlagSet(state, StatusFlags.DAMAGED_WIRE_CTRL_FLAG);
    }
    AnalogInput.isDamageWire = isDamageWire;
    function setVoltageControl(analogInput, text) {
        return {
            ...analogInput,
            name: text,
            setting_register: (analogInput.setting_register & AnalogInput.Flags.EVENT_TO_SERVER) |
                Flags.NO_GUARD_CTRL_ENABLE,
        };
    }
    AnalogInput.setVoltageControl = setVoltageControl;
    AnalogInput.ThresholdMax = {
        [Type.ANALOG_INPUT]: 35,
        [Type.PRESSURE_SENSOR_5]: 5,
        [Type.PRESSURE_SENSOR_12]: 12,
        [Type.PRESSURE_SENSOR_MLD_6]: 6,
        [Type.HUMIDITY_SENSOR]: 100,
        [Type.MAGNET]: 300,
        [Type.MOTION_CONTROLLED]: 300,
        [Type.SMOKE]: 300,
        [Type.LEAK]: 300,
        [Type.MOTION_UNCONTROLLED]: 300,
        [Type.THERMOSTAT]: 300,
        [Type.BOILER_FAILURE_PLUS]: 300,
        [Type.BOILER_FAILURE_MINUS]: 300,
        [Type.IGNITION]: 300,
        [Type.SPEED_SENSOR]: 300,
        [Type.RPM_SENSOR]: 300,
        [Type.DISCRETE]: 300,
        [Type.ALARM_BUTTON]: 300,
        [Type.FUEL_STAT_SENSOR]: 300,
        [Type.ANALOG_DISCRETE_SENSOR_NO]: 300,
        [Type.ANALOG_DISCRETE_SENSOR_NC]: 300,
        [Type.PRESSURE_SENSOR_MLD_10]: 10,
    };
    function getThresholdMax(analog_input) {
        const { sensor_type, units } = analog_input;
        if (isUseResistance(analog_input)) {
            return 500;
        }
        if (isAnalogInputType(analog_input)) {
            if (units == Units.AMPER) {
                return 100;
            }
            if (units == Units.VOLTAGE) {
                return 450;
            }
        }
        return AnalogInput.ThresholdMax[sensor_type];
    }
    AnalogInput.getThresholdMax = getThresholdMax;
    AnalogInput.analog_input_units = [
        {
            value: Units.VOLTAGE,
            axes: 'voltage',
            multiply: 10,
            value_title: 'Напряжение',
            unit: 'В',
        },
        {
            value: Units.RESISTANCE,
            axes: 'resistance',
            multiply: 1000,
            value_title: 'Сопротивление',
            unit: 'кОм',
        },
        {
            value: Units.PRESSURE,
            axes: 'pressure',
            multiply: 10,
            value_title: 'Давление',
            unit: 'бар',
        },
        {
            value: Units.SPEED,
            axes: 'speed',
            multiply: 1,
            value_title: 'Скорость',
            unit: 'км/ч',
        },
        {
            value: Units.RPM,
            axes: 'autoscan_turn',
            multiply: 10,
            value_title: 'Обороты',
            unit: 'об/мин',
        },
        {
            value: Units.VOLUME,
            axes: 'liter',
            multiply: 1,
            value_title: 'Объём',
            unit: 'л',
        },
        {
            value: Units.FUEL_RATE,
            axes: 'flowPerHour',
            multiply: 10,
            value_title: 'Расход',
            unit: 'л/ч',
        },
        {
            value: Units.PERCENT,
            axes: 'percent',
            multiply: 1,
            value_title: 'Проценты',
            unit: '%',
        },
        {
            value: Units.UNITLESS,
            axes: 'without_unit',
            multiply: 10,
            value_title: 'Без единиц',
            unit: '',
        },
        {
            value: Units.AMPER,
            axes: 'amper',
            multiply: 10,
            value_title: 'Сила тока',
            unit: 'А',
        },
        {
            value: Units.FREQUENCY,
            axes: 'herz',
            multiply: 10,
            value_title: 'Частота',
            unit: 'Гц',
        },
        {
            value: Units.TEMPERATURE,
            axes: 'temperature',
            multiply: 10,
            value_title: 'Температура',
            unit: '°C',
        },
        {
            value: Units.STREAM,
            axes: 'stream',
            multiply: 10,
            value_title: 'Поток',
            unit: 'м³/ч',
        },
        {
            value: Units.PPM,
            axes: 'ppm',
            multiply: 10,
            value_title: 'Концентрация',
            unit: 'ppm',
        },
        {
            value: Units.REACTIVE_POWER,
            axes: 'reactive_power',
            multiply: 10,
            value_title: 'Реактивная мощность',
            unit: 'ВАР',
        },
        {
            value: Units.ACTIVE_POWER,
            axes: 'active_power',
            multiply: 10,
            value_title: 'Активная мощность',
            unit: 'Вт',
        },
        {
            value: Units.FULL_POWER,
            axes: 'full_power',
            multiply: 10,
            value_title: 'Полная мощность',
            unit: 'ВА',
        },
        {
            value: Units.ENERGY,
            axes: 'energy',
            multiply: 10,
            value_title: 'Киловатт-час',
            unit: 'кВт•ч',
        },
        {
            value: Units.PHASE_SHIFT,
            axes: 'angle',
            multiply: 10,
            value_title: 'Сдвиг фазы',
            unit: '°',
        },
        {
            value: Units.MILLIAMPER,
            axes: 'milliamper',
            multiply: 10,
            value_title: 'Сила тока',
            unit: 'мА',
        },
    ];
    function getMultiplier(input) {
        return AnalogInput.analog_input_units[input.units ?? 0]?.multiply ?? 10;
    }
    AnalogInput.getMultiplier = getMultiplier;
    function getValueTitle(input) {
        return AnalogInput.analog_input_units[input.units ?? 0]?.value_title ?? 'Напряжение';
    }
    AnalogInput.getValueTitle = getValueTitle;
    function getUnit(input) {
        const type = getType(input);
        return type === Type.ANALOG_INPUT
            ? AnalogInput.analog_input_units[input.units ?? 0]?.unit
            : AnalogInput.analog_input_units[AnalogInput.default_units[type] ?? 0].unit;
    }
    AnalogInput.getUnit = getUnit;
    AnalogInput.default_units = {
        [Type.ANALOG_INPUT]: Units.VOLTAGE,
        [Type.PRESSURE_SENSOR_5]: Units.PRESSURE,
        [Type.PRESSURE_SENSOR_12]: Units.PRESSURE,
        [Type.PRESSURE_SENSOR_MLD_6]: Units.PRESSURE,
        [Type.HUMIDITY_SENSOR]: Units.PERCENT,
        [Type.MAGNET]: Units.VOLTAGE,
        [Type.MOTION_CONTROLLED]: Units.VOLTAGE,
        [Type.SMOKE]: Units.VOLTAGE,
        [Type.LEAK]: Units.VOLTAGE,
        [Type.MOTION_UNCONTROLLED]: Units.VOLTAGE,
        [Type.THERMOSTAT]: Units.VOLTAGE,
        [Type.BOILER_FAILURE_PLUS]: Units.VOLTAGE,
        [Type.BOILER_FAILURE_MINUS]: Units.VOLTAGE,
        [Type.IGNITION]: Units.VOLTAGE,
        [Type.SPEED_SENSOR]: Units.SPEED,
        [Type.RPM_SENSOR]: Units.RPM,
        [Type.DISCRETE]: Units.VOLTAGE,
        [Type.ALARM_BUTTON]: Units.VOLTAGE,
        [Type.FUEL_STAT_SENSOR]: Units.FUEL_RATE,
        [Type.ANALOG_DISCRETE_SENSOR_NO]: Units.VOLTAGE,
        [Type.ANALOG_DISCRETE_SENSOR_NC]: Units.VOLTAGE,
        [Type.PRESSURE_SENSOR_MLD_10]: Units.PRESSURE,
    };
    function setUnit(analog_input) {
        const type = getType(analog_input);
        return {
            ...analog_input,
            units: type === Type.ANALOG_INPUT ? analog_input.units : AnalogInput.default_units[type],
        };
    }
    AnalogInput.setUnit = setUnit;
    function getType(sensor) {
        return sensor.sensor_type;
    }
    AnalogInput.getType = getType;
    function isAnalogInputType(sensor) {
        return sensor.sensor_type == Type.ANALOG_INPUT;
    }
    AnalogInput.isAnalogInputType = isAnalogInputType;
    function isPressureInput(sensor) {
        return pressureTypes.includes(sensor.sensor_type);
    }
    AnalogInput.isPressureInput = isPressureInput;
    function getLimits(sensor) {
        return {
            high: 
            //@ts-ignore
            sensor.upper_threshold === '' || sensor.upper_threshold == null
                ? null
                : parseFloat(sensor.upper_threshold + ''),
            low: 
            //@ts-ignore
            sensor.lower_threshold === '' || sensor.lower_threshold == null
                ? null
                : parseFloat(sensor.lower_threshold + ''),
        };
    }
    AnalogInput.getLimits = getLimits;
    function convertThresholds(sensor) {
        return {
            ...sensor,
            upper_threshold: convertThreshold(sensor.upper_threshold),
            lower_threshold: convertThreshold(sensor.lower_threshold),
        };
    }
    AnalogInput.convertThresholds = convertThresholds;
    function setIsAmperInputType(analog_input, value) {
        return setFlag(analog_input, Flags.IS_AMPER_INPUT, value);
    }
    AnalogInput.setIsAmperInputType = setIsAmperInputType;
    function isAmperInputType(analog_input) {
        return isFlagSet(analog_input, Flags.IS_AMPER_INPUT);
    }
    AnalogInput.isAmperInputType = isAmperInputType;
    AnalogInput.get = createAccessors(arrayGetSet('analog_inputs'), {
        name: 'Датчик',
        notification_list: [],
        upper_notification_list: [],
        physical_input_num: -1,
        sensor_type: AnalogInput.Type.ANALOG_INPUT,
        recovery_list: [],
        upper_threshold: 0,
        lower_threshold: 0,
        active_level_timeout: 1,
        inactive_level_timeout: 2,
        setting_register: 0,
        is_new: true,
        impulse_frequency: 1,
        calibration: null,
        units: 0,
        position: 0,
    });
})(AnalogInput || (AnalogInput = {}));
